import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Link } from "react-router-dom";
import { useApi } from "../../contexts/ApiContext";
import { useFeatures } from "../../contexts/FeatureContext";
import { buildRatingTimeline, buildChartData } from "../../utils/eloHistory";
import "../../styles/crud.css";
import "../setup/SetupScreen.css";
import "./StatisticsPage.css";

// Distinct, theme-friendly line colors (cycled if more players are selected).
const COLORS = [
  "#0a1f5c",
  "#e07a3f",
  "#1a7a45",
  "#c0392b",
  "#8e44ad",
  "#2c7be5",
  "#d4a017",
  "#16a085",
  "#e84393",
  "#636e72",
];

// Tooltip: players sorted by rating (highest first), no tournament label.
function RatingTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const rows = [...payload].sort((a, b) => b.value - a.value);
  return (
    <div className="stat-tooltip">
      {rows.map((r) => (
        <div key={r.dataKey} className="stat-tooltip-row">
          <span
            className="stat-tooltip-swatch"
            style={{ background: r.color || r.stroke }}
          />
          <span className="stat-tooltip-name">{r.name}</span>
          <span className="stat-tooltip-val">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function StatisticsPage() {
  const { playersApi, matchResultsApi } = useApi();
  const { flags } = useFeatures();
  const ratingsOn = flags.ratingsEnabled;

  const [selected, setSelected] = useState([]); // array of player names
  const [focused, setFocused] = useState(null); // spotlighted player (or null)
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { data: players = [] } = useQuery({
    queryKey: ["players"],
    queryFn: () => playersApi.getAll(),
  });

  const { data: results = [] } = useQuery({
    queryKey: ["matchResults", "all"],
    queryFn: () => matchResultsApi.getAll(),
    enabled: ratingsOn,
  });

  useEffect(() => {
    function onClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const eloByName = useMemo(() => {
    const map = {};
    players.forEach((p) => (map[p.name] = p.elo ?? p.Elo ?? null));
    return map;
  }, [players]);

  const timeline = useMemo(() => buildRatingTimeline(results), [results]);
  const chartData = useMemo(
    () => buildChartData(timeline, selected),
    [timeline, selected],
  );

  const selectedSet = new Set(selected);
  const filtered = players
    .filter((p) => !selectedSet.has(p.name))
    .filter(
      (p) =>
        search === "" || p.name.toLowerCase().startsWith(search.toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 10);
  const availableCount = players.filter((p) => !selectedSet.has(p.name)).length;

  function addPlayer(name) {
    setSelected((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setSearch("");
    setOpen(false);
  }
  function addAll() {
    setSelected((prev) => {
      const set = new Set(prev);
      players.forEach((p) => set.add(p.name));
      return Array.from(set);
    });
    setSearch("");
    setOpen(false);
  }
  function removePlayer(name) {
    setSelected((prev) => prev.filter((n) => n !== name));
    setFocused((prev) => (prev === name ? null : prev));
  }

  return (
    <div className="crud-page">
      <div className="crud-header">
        <h1 className="crud-title">Statistics</h1>
        <p className="crud-sub">
          Compare players' rating history across tournaments.
        </p>
      </div>

      {!ratingsOn ? (
        <div className="tn-form-card">
          <p className="crud-empty">
            Ratings are turned off.{" "}
            <Link className="stat-enable-link" to="/app/settings">
              Enable ratings in Settings
            </Link>{" "}
            to track and compare player rating history.
          </p>
        </div>
      ) : (
        <div className="tn-form-card">
          {/* ── Player search ── */}
          <div className="tn-search-group" ref={dropdownRef}>
            <label className="tn-search-label">Players</label>
            <div className="tn-dropdown-wrap">
              <input
                className="tn-input"
                placeholder="Search players to compare..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
              />
              {open && (filtered.length > 0 || availableCount > 0) && (
                <div className="tn-dropdown">
                  {availableCount > 0 && (
                    <div
                      className="tn-dropdown-item tn-dropdown-addall"
                      onMouseDown={addAll}
                    >
                      ＋ Add all players ({availableCount})
                    </div>
                  )}
                  {filtered.map((p) => (
                    <div
                      key={p.id}
                      className="tn-dropdown-item"
                      onMouseDown={() => addPlayer(p.name)}
                    >
                      {p.name}
                      {p.elo != null && (
                        <span className="tn-elo-badge">{p.elo}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Selected players ── */}
          {selected.length > 0 && (
            <div className="tn-list">
              {selected.map((name, i) => (
                <div key={name} className="tn-item">
                  <span
                    className="stat-swatch"
                    style={{ background: COLORS[i % COLORS.length] }}
                  />
                  <span className="tn-name">{name}</span>
                  {eloByName[name] != null && (
                    <span className="tn-elo-badge">{eloByName[name]}</span>
                  )}
                  <button className="tn-del" onClick={() => removePlayer(name)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Rating chart ── */}
          <div className="stat-chart">
            {selected.length === 0 ? (
              <p className="crud-empty">
                Search and select players above to plot their ratings.
              </p>
            ) : timeline.length === 0 ? (
              <p className="crud-empty">
                No completed matches yet — play some tournaments to build rating
                history.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={420}>
                <LineChart
                  data={chartData}
                  margin={{ top: 12, right: 24, left: 0, bottom: 56 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border-light)"
                  />
                  <XAxis
                    dataKey="label"
                    angle={-30}
                    textAnchor="end"
                    height={60}
                    interval="preserveStartEnd"
                    tick={{ fontSize: 12, fill: "var(--color-text-subtle)" }}
                  />
                  <YAxis
                    domain={["dataMin - 40", "dataMax + 40"]}
                    width={48}
                    tick={{ fontSize: 12, fill: "var(--color-text-subtle)" }}
                  />
                  <Tooltip content={<RatingTooltip />} />
                  <Legend
                    onClick={(o) =>
                      setFocused((prev) =>
                        prev === (o.dataKey ?? o.value)
                          ? null
                          : o.dataKey ?? o.value,
                      )
                    }
                    wrapperStyle={{ cursor: "pointer" }}
                  />
                  {selected.map((name, i) => {
                    const dim = focused && focused !== name;
                    const opacity = dim ? 0.22 : 1;
                    return (
                      <Line
                        key={name}
                        type="monotone"
                        dataKey={name}
                        stroke={COLORS[i % COLORS.length]}
                        strokeWidth={focused === name ? 3 : 2}
                        strokeOpacity={opacity}
                        dot={{ r: 3, opacity }}
                        activeDot={{ r: 5 }}
                        connectNulls={false}
                        onClick={() =>
                          setFocused((prev) => (prev === name ? null : name))
                        }
                        style={{ cursor: "pointer" }}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StatisticsPage;
